import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ControlCenterIcon } from "../assets/svgs/sf-symbols/switch.2.svg";
import { ReactComponent as Apple } from "../assets/svgs/sf-symbols/apple.logo.svg";
import { ReactComponent as Player } from "../assets/svgs/sf-symbols/play.circle.svg";
import { ReactComponent as Search } from "../assets/svgs/sf-symbols/magnifyingglass.svg";
import ControlCenter from "./ControlCenter";
import { NowPlayingExpanded } from "./NowPlaying";
import LiquidGlass from "./LiquidGlass";
import { useSelector } from "react-redux";
import { notify } from "./Notification";
import useClock, { formatMenuBarDate } from "../hooks/useClock";
import useBattery from "../hooks/useBattery";
import BatteryMenu, { BatteryGlyph } from "./BatteryMenu";
import Spotlight from "./Spotlight";
import NotificationCenter from "./NotificationCenter";
import WifiMenu, { WifiGlyph } from "./WifiMenu";
import useWifi, { HOTSPOT } from "../hooks/useWifi";

const MenuBar = () => {
  const [show, setShow] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showBattery, setShowBattery] = useState(false);
  const [batteryMenuLeft, setBatteryMenuLeft] = useState(0);
  const [showWifi, setShowWifi] = useState(false);
  const [wifiMenuLeft, setWifiMenuLeft] = useState(0);
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [showNC, setShowNC] = useState(false);
  const [energyMode, setEnergyMode] = useState("automatic");
  const lowPower = energyMode === "low";
  const { settings, currentApp } = useSelector((state) => state.app);
  const now = useClock();
  const battery = useBattery();
  const wifiState = useWifi();
  const ref = useRef();
  const playerRef = useRef();
  const batteryRef = useRef();
  const triggerRef = useRef();
  const playerTriggerRef = useRef();
  const batteryTriggerRef = useRef();
  const wifiRef = useRef();
  const wifiTriggerRef = useRef();
  const ncRef = useRef();
  const ncTriggerRef = useRef();

  useClickOutside(ref, () => setShow(false), [triggerRef]);
  useClickOutside(playerRef, () => setShowPlayer(false), [playerTriggerRef]);
  useClickOutside(batteryRef, () => setShowBattery(false), [batteryTriggerRef]);
  useClickOutside(wifiRef, () => setShowWifi(false), [wifiTriggerRef]);
  useClickOutside(ncRef, () => setShowNC(false), [ncTriggerRef]);

  // Opening Spotlight closes the other menus.
  const openSpotlight = (next) => {
    setShowSpotlight(next);
    if (next) {
      setShow(false);
      setShowPlayer(false);
      setShowBattery(false);
      setShowWifi(false);
      setShowNC(false);
    }
  };

  // Escape closes whichever menu is open. ⌘K / Ctrl+K / Ctrl+Space toggle Spotlight
  // (⌘Space itself never reaches the page: macOS keeps it for its own Spotlight).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowBattery(false);
        setShowWifi(false);
        setShowNC(false);
        setShowPlayer(false);
        setShowSpotlight(false);
        return;
      }
      const k = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      const space = e.ctrlKey && e.code === "Space";
      if (k || space) {
        e.preventDefault();
        setShowSpotlight((open) => {
          if (!open) {
            setShow(false);
            setShowPlayer(false);
            setShowBattery(false);
            setShowWifi(false);
            setShowNC(false);
          }
          return !open;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Like macOS, a menu drops from its item's left edge, kept on screen.
  const menuLeft = (trigger) => Math.min(trigger.current.getBoundingClientRect().left, window.innerWidth - 316);

  const toggleWifi = () => {
    setWifiMenuLeft(menuLeft(wifiTriggerRef));
    setShowWifi(!showWifi);
  };

  const toggleBattery = () => {
    setBatteryMenuLeft(menuLeft(batteryTriggerRef));
    setShowBattery(!showBattery);
  };


  return (
    <>
      <nav className="menubar no-select" aria-label="Menu bar">
        <div className="menubar-group">
          <span className="menubar-item">
            <Apple className="w-[13px] h-[16px]" />
          </span>
          <span className="menubar-item font-bold">{currentApp}</span>
          {settings.map((ele) => (
            <span key={ele} className="menubar-item">
              {ele}
            </span>
          ))}
        </div>

        <div className="menubar-group">
          <button
            ref={playerTriggerRef}
            type="button"
            onClick={() => setShowPlayer(!showPlayer)}
            className={`menubar-item ${showPlayer ? "is-open" : ""}`}
            aria-label="Now Playing"
            aria-expanded={showPlayer}
          >
            <Player className="w-[15px]" />
          </button>
          <button
            ref={batteryTriggerRef}
            type="button"
            onClick={toggleBattery}
            className={`menubar-item ${showBattery ? "is-open" : ""}`}
            aria-label={`Battery, ${Math.round(battery.level * 100)}%${lowPower ? ", Low Power Mode" : ""}`}
            aria-expanded={showBattery}
          >
            <BatteryGlyph battery={battery} lowPower={lowPower} className="w-[25px]" />
          </button>
          <button
            ref={wifiTriggerRef}
            type="button"
            onClick={toggleWifi}
            className={`menubar-item ${showWifi ? "is-open" : ""}`}
            aria-label={`Wi-Fi, ${
              !wifiState.on ? "off" : wifiState.connecting ? "connecting" : wifiState.current || "not connected"
            }`}
            aria-expanded={showWifi}
          >
            <WifiGlyph
              on={wifiState.on}
              connecting={!!wifiState.connecting}
              hotspot={wifiState.current === HOTSPOT.name}
              className="w-[17px] h-[13px]"
            />
          </button>
          <button
            type="button"
            onClick={() => openSpotlight(!showSpotlight)}
            className={`menubar-item ${showSpotlight ? "is-open" : ""}`}
            aria-label="Spotlight"
            aria-expanded={showSpotlight}
            title="Spotlight (⌘K)"
          >
            <Search className="w-[14px]" />
          </button>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setShow(!show)}
            className={`menubar-item ${show ? "is-open" : ""}`}
            aria-label="Control Center"
            aria-expanded={show}
            data-testid="cc-trigger"
          >
            <ControlCenterIcon className="w-[16px] h-[16px]" />
          </button>
          <button
            ref={ncTriggerRef}
            type="button"
            onClick={() => setShowNC(!showNC)}
            className={`menubar-item tabular-nums ${showNC ? "is-open" : ""}`}
            aria-label={`${formatMenuBarDate(now)}. Notification Center`}
            aria-expanded={showNC}
          >
            {formatMenuBarDate(now)}
          </button>
        </div>
      </nav>

      <div
        ref={playerRef}
        className={`menu-panel cc-panel cc-panel--np !right-[150px] ${showPlayer ? "is-open" : ""}`}
        aria-hidden={!showPlayer}
      >
        <LiquidGlass radius={24} className="cc-item">
          <NowPlayingExpanded />
        </LiquidGlass>
      </div>

      <div
        ref={batteryRef}
        className={`mac-menu ${showBattery ? "is-open" : ""}`}
        style={{ left: batteryMenuLeft }}
        aria-hidden={!showBattery}
      >
        <BatteryMenu
          battery={battery}
          energyMode={energyMode}
          onEnergyModeChange={setEnergyMode}
          onOpenSettings={() => {
            setShowBattery(false);
            notify("System Settings isn't available on this Mac. Open Notes to read my resume.");
          }}
        />
      </div>

      <div
        ref={wifiRef}
        className={`mac-menu ${showWifi ? "is-open" : ""}`}
        style={{ left: wifiMenuLeft }}
        aria-hidden={!showWifi}
      >
        <WifiMenu
          open={showWifi}
          onClose={() => setShowWifi(false)}
          onOpenSettings={() => {
            setShowWifi(false);
            notify("System Settings isn't available on this Mac. Open Notes to read my resume.");
          }}
        />
      </div>

      <NotificationCenter open={showNC} onClose={() => setShowNC(false)} panelRef={ncRef} />

      <Spotlight
        open={showSpotlight}
        onClose={() => setShowSpotlight(false)}
        onOpenControlCenter={() => setShow(true)}
        lowPower={lowPower}
        onToggleLowPower={() => setEnergyMode(lowPower ? "automatic" : "low")}
      />

      <div
        ref={ref}
        className={`menu-panel cc-panel ${show ? "is-open" : ""}`}
        aria-hidden={!show}
      >
        <ControlCenter open={show} />
      </div>
    </>
  );
};

export default MenuBar;

// Calls `callback` on a mousedown outside `ref`, ignoring clicks inside any of `excludedRefs`
// (the buttons that toggle the panel, so they can close it themselves).
const useClickOutside = (ref, callback, excludedRefs = []) => {
  const latest = useRef();
  latest.current = { callback, excludedRefs };

  useEffect(() => {
    const handleClick = (e) => {
      const { callback, excludedRefs } = latest.current;
      const isExcluded = excludedRefs.some((r) => r.current && r.current.contains(e.target));
      if (!isExcluded && ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [ref]);
};
